import React, { useState } from "react";
import toast from "react-hot-toast";
import { JOB_MODES, JOB_MODE_LABELS, EVENT_TYPES, EVENT_TYPE_LABELS, confirmQuestionsUpload } from "../services/jobService";
import { QuestionSourceManager } from "./QuestionGenControls";
import { InterviewQuestionSourceManager } from "./InterviewQuestionSourceManager";
import "./RecruiterJobs.css";
import "./QuestionBankManager.css";

const initialForm = {
  title: "",
  companyName: "",
  eventType: EVENT_TYPES.INTERVIEW,
  mode: JOB_MODES.EXACT,
  role: "",
  jdText: "",
  difficulty: "moderate",
  interviewQuestions: [],
  mcqQuestions: [],
  codingQuestions: [],
  config: {}
};

// One consistent shell for every assessment type: pick a type, then author
// its questions right here — Write manually | (Upload Excel/CSV) | Generate
// with AI — with the result always shown as an editable card list, whether
// the job is MCQ, Coding, or AI Interview.
export default function CreateJobModal({ open, saving, onClose, onCreate }) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Set once the job itself is successfully created — a retry after a
  // partial question-save failure reuses this instead of creating a SECOND
  // job. Previously, any question-save failure closed and reset the whole
  // form regardless, discarding every question the recruiter had typed
  // (valid or not) with no way to recover it short of retyping everything
  // via "Manage Questions" on the now-empty job that was left behind.
  const [createdJobId, setCreatedJobId] = useState(null);

  if (!open) {
    return null;
  }

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateConfig = (field, value) => {
    setForm((current) => ({ ...current, config: { ...current.config, [field]: value } }));
  };

  const updateList = (field, updater) => {
    setForm((current) => ({ ...current, [field]: updater(current[field]) }));
  };

  const handleClose = () => {
    setForm(initialForm);
    setError("");
    setCreatedJobId(null);
    onClose();
  };

  // Saves one question list, removes the ones that saved successfully from
  // the form (so a later retry only resubmits what's actually still
  // outstanding), and returns the errors for whichever rows didn't save —
  // each still tagged with the review note the backend gave for it, so the
  // recruiter can see exactly what to fix instead of a generic failure.
  const saveQuestionList = async (jobId, kind, questions, listField) => {
    const result = await confirmQuestionsUpload(jobId, kind, questions);
    const errors = result.errors || [];
    if (errors.length) {
      const failedIndexes = new Set(errors.map((e) => e.row - 1));
      updateList(listField, (prev) => prev.filter((_, i) => failedIndexes.has(i)));
    } else {
      updateList(listField, () => []);
    }
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    // These "must have added something" checks only make sense on the FIRST
    // attempt — on a retry, a list can legitimately be empty because
    // everything in it already saved successfully last time, and all
    // that's left outstanding is the OTHER question type.
    if (!createdJobId) {
      if (!form.title.trim()) {
        setError("Job title is required.");
        return;
      }
      if (!form.companyName.trim()) {
        setError("Company name is required.");
        return;
      }
      if (form.eventType === EVENT_TYPES.INTERVIEW && form.mode !== JOB_MODES.RANDOM && form.interviewQuestions.length === 0) {
        setError("Add at least one question for this interview mode.");
        return;
      }
      if (form.eventType === EVENT_TYPES.MCQ && form.mcqQuestions.length === 0) {
        setError("Add at least one MCQ question.");
        return;
      }
      if (form.eventType === EVENT_TYPES.MCQ && form.config?.include_coding && form.codingQuestions.length === 0) {
        setError("Add at least one coding question for the coding round.");
        return;
      }
      if (form.eventType === EVENT_TYPES.CODING && form.codingQuestions.length === 0) {
        setError("Add at least one coding question.");
        return;
      }
    }

    setSubmitting(true);
    let jobId = createdJobId;
    if (!jobId) {
      try {
        jobId = await onCreate({
          title: form.title,
          companyName: form.companyName,
          eventType: form.eventType,
          mode: form.mode,
          uploadedQuestions: form.eventType === EVENT_TYPES.INTERVIEW ? form.interviewQuestions : [],
          config: {
            ...form.config,
            ...(form.eventType === EVENT_TYPES.INTERVIEW
              ? { role: form.role.trim(), jd_text: form.jdText, difficulty: form.difficulty }
              : {})
          }
        });
        setCreatedJobId(jobId);
      } catch (err) {
        setError(err.message || "Unable to create job.");
        setSubmitting(false);
        return;
      }
    }

    try {
      let allErrors = [];
      if (form.eventType === EVENT_TYPES.MCQ) {
        if (form.mcqQuestions.length) {
          allErrors = allErrors.concat(await saveQuestionList(jobId, "mcq", form.mcqQuestions, "mcqQuestions"));
        }
        if (form.config?.include_coding && form.codingQuestions.length) {
          allErrors = allErrors.concat(await saveQuestionList(jobId, "coding", form.codingQuestions, "codingQuestions"));
        }
      } else if (form.eventType === EVENT_TYPES.CODING) {
        if (form.codingQuestions.length) {
          allErrors = allErrors.concat(await saveQuestionList(jobId, "coding", form.codingQuestions, "codingQuestions"));
        }
      }

      if (allErrors.length) {
        // Deliberately NOT closing/resetting the form — the job and every
        // question that DID save are already safe; only the questions still
        // shown below (the ones that failed) need fixing before retrying.
        setError(
          `Job saved — but ${allErrors.length} question${allErrors.length === 1 ? "" : "s"} could not be saved: ` +
          allErrors.map((e) => `Row ${e.row}: ${e.message}`).join(" · ") +
          ". Fix the question(s) below (still shown in the list) and click Retry — everything else is already saved."
        );
      } else {
        toast.success("Job created");
        handleClose();
      }
    } catch (err) {
      // A hard failure (network error, auth issue) rather than a per-row
      // validation problem — same principle: the job already exists, so
      // don't destroy what's still in the form, just let them retry.
      setError(
        `Job saved, but saving its questions failed: ${err.message || "unknown error"}. ` +
        "Nothing has been lost — click Retry to try saving these questions again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const busy = saving || submitting;

  return (
    <div className="recruiter-modal-backdrop" role="presentation">
      <div className="recruiter-modal recruiter-modal--wide" role="dialog" aria-modal="true" aria-labelledby="create-job-title">
        <div className="recruiter-modal-header">
          <div>
            <h2 id="create-job-title">Create Job</h2>
            <p>Add a candidate-ready assessment link.</p>
          </div>
          <button className="icon-button" type="button" onClick={handleClose} aria-label="Close">
            x
          </button>
        </div>

        <form className="job-form" onSubmit={handleSubmit}>
          {error && <div className="job-form-error">{error}</div>}

          <label>
            <span>Job Title</span>
            <input
              value={form.title}
              onChange={(event) => updateField("title", event.target.value)}
              placeholder="Frontend Engineer"
            />
          </label>

          <label>
            <span>Company Name</span>
            <input
              value={form.companyName}
              onChange={(event) => updateField("companyName", event.target.value)}
              placeholder="Vagmee"
            />
          </label>

          <label>
            <span>Assessment Type</span>
            <div className="inst-type-toggle">
              {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`inst-type-btn${form.eventType === value ? " active" : ""}`}
                  onClick={() => updateField("eventType", value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </label>

          {form.eventType === EVENT_TYPES.MCQ && (
            <label className="qbm-checkbox-row">
              <input
                type="checkbox"
                checked={!!form.config?.include_coding}
                onChange={(event) => updateConfig("include_coding", event.target.checked)}
              />
              <span>Also include a coding round after the MCQ</span>
            </label>
          )}

          {(form.eventType === EVENT_TYPES.MCQ || form.eventType === EVENT_TYPES.CODING) && (
            <label className="qbm-checkbox-row">
              <input
                type="checkbox"
                checked={!!form.config?.proctoring_disabled}
                onChange={(event) => updateConfig("proctoring_disabled", event.target.checked)}
              />
              <span>Disable proctoring for this test (no camera, screen share, or fullscreen requirement)</span>
            </label>
          )}

          {(form.eventType === EVENT_TYPES.MCQ || form.eventType === EVENT_TYPES.CODING) && (
            <label>
              <span>Total test time (minutes)</span>
              <input
                type="number"
                min="1"
                value={form.config?.total_minutes || ""}
                onChange={(event) => {
                  const raw = event.target.value;
                  updateConfig("total_minutes", raw === "" ? null : Math.max(1, parseInt(raw, 10) || 1));
                }}
                placeholder={
                  form.eventType === EVENT_TYPES.MCQ && form.config?.include_coding
                    ? "e.g. 90 — shared across MCQ + Coding, leave blank for no hard limit"
                    : "e.g. 60 — leave blank for no hard limit"
                }
              />
            </label>
          )}

          {form.eventType === EVENT_TYPES.INTERVIEW && (
            <label>
              <span>Interview Mode</span>
              <select
                value={form.mode}
                onChange={(event) => updateField("mode", event.target.value)}
              >
                {Object.entries(JOB_MODE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
          )}

          {form.eventType === EVENT_TYPES.INTERVIEW && (
            <div className="job-form-block">
              <span className="job-form-block-label">Questions</span>
              <InterviewQuestionSourceManager
                companyName={form.companyName}
                role={form.role} onRoleChange={(v) => updateField("role", v)}
                jdText={form.jdText} onJdTextChange={(v) => updateField("jdText", v)}
                difficulty={form.difficulty} onDifficultyChange={(v) => updateField("difficulty", v)}
                questions={form.interviewQuestions}
                onQuestionsChange={(qs) => updateList("interviewQuestions", () => qs)}
              />
            </div>
          )}

          {form.eventType === EVENT_TYPES.MCQ && (
            <div className="job-form-block">
              <span className="job-form-block-label">MCQ Questions</span>
              <QuestionSourceManager
                eventType="MCQ"
                companyName={form.companyName}
                questions={form.mcqQuestions}
                onAdd={(q) => updateList("mcqQuestions", (prev) => [...prev, q])}
                onUpdate={(i, existing, updated) => updateList("mcqQuestions", (prev) => prev.map((x, idx) => (idx === i ? updated : x)))}
                onRemove={(i) => updateList("mcqQuestions", (prev) => prev.filter((_, idx) => idx !== i))}
                onBulkAdd={(items) => updateList("mcqQuestions", (prev) => [...prev, ...items])}
              />
            </div>
          )}

          {((form.eventType === EVENT_TYPES.MCQ && form.config?.include_coding) || form.eventType === EVENT_TYPES.CODING) && (
            <div className="job-form-block">
              <span className="job-form-block-label">Coding Questions</span>
              <QuestionSourceManager
                eventType="CODING"
                companyName={form.companyName}
                questions={form.codingQuestions}
                onAdd={(q) => updateList("codingQuestions", (prev) => [...prev, q])}
                onUpdate={(i, existing, updated) => updateList("codingQuestions", (prev) => prev.map((x, idx) => (idx === i ? updated : x)))}
                onRemove={(i) => updateList("codingQuestions", (prev) => prev.filter((_, idx) => idx !== i))}
                onBulkAdd={(items) => updateList("codingQuestions", (prev) => [...prev, ...items])}
              />
            </div>
          )}

          <div className="recruiter-modal-actions">
            <button className="secondary-button" type="button" onClick={handleClose}>
              Cancel
            </button>
            <button className="primary-button" type="submit" disabled={busy}>
              {busy ? "Saving..." : createdJobId ? "Retry Saving Questions" : "Create Job"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
